/**
 * EventOddsWidget — View odds for a specific event
 * Extracted from: fetchEventOdds() lines 2439-2498, getEventOdds() lines 2432-2436
 * API: GET /event-odds/:sport/:eventId (regions x markets tokens)
 * CSS prefix: eow-
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { fetchOddsSports, fetchEventOdds } from '../lib/api'
import { getSportIcon } from '../lib/oddsConstants'
import OddsTable from './shared/OddsTable'
import { Search, RefreshCw, ChevronRight } from 'lucide-react'
import './EventOddsWidget.css'

const REGIONS = [
  { id: 'us', label: 'US' },
  { id: 'us2', label: 'US2' },
  { id: 'uk', label: 'UK' },
  { id: 'eu', label: 'EU' },
  { id: 'au', label: 'AU' },
]

const MARKETS = [
  { id: 'h2h', label: 'Moneyline' },
  { id: 'spreads', label: 'Spreads' },
  { id: 'totals', label: 'Totals' },
]

function EventOddsWidget({ onQuotaUpdate, initialSport, initialEventId }) {
  const [sports, setSports] = useState([])
  const [selectedSport, setSelectedSport] = useState('')
  const [eventId, setEventId] = useState('')
  const [selectedRegions, setSelectedRegions] = useState(new Set(['us']))
  const [selectedMarkets, setSelectedMarkets] = useState(new Set(['h2h']))
  const [additionalMarkets, setAdditionalMarkets] = useState('')
  const [oddsData, setOddsData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [sportsLoading, setSportsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [expanded, setExpanded] = useState(true)
  const [lastFetch, setLastFetch] = useState(null)
  const lastAutoTargetRef = useRef('')

  useEffect(() => {
    async function loadSports() {
      setSportsLoading(true)
      const res = await fetchOddsSports()
      if (res.status === 'ok' && res.data) {
        setSports(res.data.filter(s => s.active))
      }
      setSportsLoading(false)
    }
    loadSports()
  }, [])

  const toggleRegion = (id) => {
    setSelectedRegions(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleMarket = (id) => {
    setSelectedMarkets(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const tokenCost = selectedRegions.size * selectedMarkets.size

  const fetchData = useCallback(async (override = {}) => {
    const sport = override.sport ?? selectedSport
    const targetEventId = (override.eventId ?? eventId).trim()

    if (!sport || !targetEventId) return
    if (selectedRegions.size === 0 || selectedMarkets.size === 0) return

    setLoading(true)
    setError(null)

    let marketsStr = Array.from(selectedMarkets).join(',')
    if (additionalMarkets.trim()) {
      marketsStr += ',' + additionalMarkets.trim()
    }

    const params = {
      regions: Array.from(selectedRegions).join(','),
      markets: marketsStr,
      oddsFormat: 'american',
    }

    const res = await fetchEventOdds(sport, targetEventId, params)

    if (res.status === 'ok' && res.data) {
      setOddsData(res.data)
      setLastFetch(new Date())
      if (onQuotaUpdate && (res.remaining || res.used)) onQuotaUpdate(res.remaining, res.used)
    } else {
      setError(res.message || 'Failed to load event odds')
      setOddsData(null)
    }

    setLoading(false)
  }, [selectedSport, eventId, selectedRegions, selectedMarkets, additionalMarkets, onQuotaUpdate])

  useEffect(() => {
    if (!initialSport || !initialEventId) return

    const targetKey = `${initialSport}::${initialEventId}`
    if (lastAutoTargetRef.current === targetKey) return

    lastAutoTargetRef.current = targetKey
    setExpanded(true)
    setSelectedSport(initialSport)
    setEventId(initialEventId)
    fetchData({ sport: initialSport, eventId: initialEventId })
  }, [initialSport, initialEventId, fetchData])

  return (
    <div className="widget-card">
      <div
        className="widget-header eow-header-clickable"
        onClick={() => setExpanded(e => !e)}
      >
        <Search size={16} className="widget-icon" />
        <span className="widget-title">Event Odds</span>
        <span className="status-badge warning" style={{ fontSize: 9 }}>
          {tokenCost} TOKEN{tokenCost !== 1 ? 'S' : ''}
        </span>
        <ChevronRight
          size={16}
          className={`eow-chevron ${expanded ? 'rotated' : ''}`}
        />
      </div>

      {expanded && (
        <>
          <div className="eow-controls">
            <div className="eow-row">
              <select
                className="eow-select"
                value={selectedSport}
                onChange={(e) => setSelectedSport(e.target.value)}
                disabled={sportsLoading}
              >
                <option value="">{sportsLoading ? 'Loading...' : 'Select sport'}</option>
                {sports.map(s => (
                  <option key={s.key} value={s.key}>
                    {getSportIcon(s.key)} {s.title}
                  </option>
                ))}
              </select>

              <input
                type="text"
                className="eow-input"
                placeholder="Event ID (from Events tab)"
                value={eventId}
                onChange={(e) => setEventId(e.target.value)}
              />
            </div>

            <div className="eow-row">
              <div className="eow-checkbox-group">
                <span className="eow-group-label">Regions:</span>
                {REGIONS.map(r => (
                  <label key={r.id} className="eow-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedRegions.has(r.id)}
                      onChange={() => toggleRegion(r.id)}
                    />
                    <span>{r.label}</span>
                  </label>
                ))}
              </div>

              <div className="eow-checkbox-group">
                <span className="eow-group-label">Markets:</span>
                {MARKETS.map(m => (
                  <label key={m.id} className="eow-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedMarkets.has(m.id)}
                      onChange={() => toggleMarket(m.id)}
                    />
                    <span>{m.label}</span>
                  </label>
                ))}
              </div>

              <button
                className="eow-fetch-btn"
                onClick={fetchData}
                disabled={!selectedSport || !eventId.trim() || selectedRegions.size === 0 || selectedMarkets.size === 0 || loading}
              >
                {loading ? <RefreshCw size={13} className="spinning" /> : <Search size={13} />}
                <span>Get Event Odds</span>
              </button>
            </div>

            <div className="eow-row eow-props-row">
              <input
                type="text"
                className="eow-input eow-props-input"
                placeholder="Additional markets (e.g. player_points,player_assists)"
                value={additionalMarkets}
                onChange={(e) => setAdditionalMarkets(e.target.value)}
              />
              <span className="eow-props-hint">
                Available: player_points, player_rebounds, player_assists, player_threes,
                player_blocks_steals, player_pass_tds, player_rush_yds, alternate_spreads,
                alternate_totals, btts, draw_no_bet, double_chance
              </span>
            </div>
          </div>

          {error && (
            <div className="widget-body">
              <div className="widget-error">{error}</div>
            </div>
          )}

          {loading && (
            <div className="widget-body">
              <div className="widget-empty">Loading event odds...</div>
            </div>
          )}

          {!loading && oddsData && (
            <OddsTable games={[oddsData]} />
          )}

          {!loading && !oddsData && !error && (
            <div className="widget-body">
              <div className="widget-empty">
                Enter a sport and event ID, then click Get Event Odds.
                <br />
                <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                  Tip: Copy event IDs from the Events Browser tab.
                </span>
              </div>
            </div>
          )}

          <div className="widget-footer">
            <span>{lastFetch ? `Updated ${lastFetch.toLocaleTimeString()}` : 'Not loaded'}</span>
            <span>Cost: {tokenCost} token{tokenCost !== 1 ? 's' : ''} per request</span>
          </div>
        </>
      )}
    </div>
  )
}

export default EventOddsWidget
